import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { createSelector } from 'reselect'

import { useTheme } from '@material-ui/styles'
import { Button, Chip, Divider, Paper, Grid, Typography, Tooltip, IconButton, makeStyles, Box } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'

import { ArrowForward, ArrowBack, MoreHoriz } from '@material-ui/icons'

import useViewportSizes from '~/components/useViewportSize'
import WhatsappFormDialog from '~/containers/WhatsAppForm'
import StoreContacts from '~/components/StoreContacts'
import { useLiterals } from '~/components/Literals'
import SegmentTag from '~/components/SegmentTag'
import SimpleBar from 'simplebar-react'

import track, { cliqueRota, cliqueWhats, log, verMaisProdutos } from '~/track'

import { selectStores } from '~/selectors'

import MobileGmbStoreDetails from './MobileGmbStoreDetails'
import { convertPeriod } from './utils/convert-period'
import ProductListDialog from './ProductsListDialog'
import ButtonStoreRoute from './ButtonStoreRoute'
import ProductsGrid from './ProductsGrid'

import api from '~/api'
import actions from '~/actions'

import GmbAttributesPreview from './GmbAttributesPreview'
import GmbAttributesDetailsDialog from './GmbAttributesDetailsDialog'
import StorePeriod from './StorePeriod'
import { StoreDialogPeriod } from './StoreDialogPeriod'

const selector = createSelector(
  (state) => state.config,
  (state) => state.app.currentProduct,
  (config, currentProduct) => ({ config, currentProduct }),
)

const useStyles = makeStyles({
  back: {
    color: '#333',
    marginRight: 4,
  },
})

function StoreInformations({ history, position }) {
  const theme = useTheme()
  const viewport = useViewportSizes()
  const classes = useStyles()
  const dispatch = useDispatch()

  const { currentStore, showStoreInformation, isMobileStoreInformationsFullHeight } = useSelector(selectStores)
  const { config, currentProduct } = useSelector(selector)
  const { getLiteral } = useLiterals()

  const simpleBarRef = useRef()
  const [didMount, setDidMount] = useState(false)
  const [products, setProducts] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [openForm, setOpenForm] = useState(false)
  const [openMobileProducts, setOpenMobileProducts] = useState(false)
  const [showListProducts, setShowListProducts] = useState(false)
  const [openGmbAttribute, setOpenGmbAttribute] = useState(false)
  const [gmbAttributes, setGmbAttributes] = useState([])

  const [isLoadingGmb, setIsLoadingGmb] = useState(false)
  const [images, setImages] = useState([])
  const [period, setPeriod] = useState(null)
  const [isOpenPeriod, setIsOpenPeriod] = useState(false)

  const isMobile = useMemo(
    () => viewport.width < theme.gofind.desktopMaxWidth,
    [viewport.width, theme.gofind.desktopMaxWidth],
  )

  const width = isMobile ? '100%' : viewport.width - theme.gofind.asideWidth - 30

  useEffect(() => {
    setDidMount(true)

    return () => setDidMount(false)
  }, [])

  useEffect(() => {
    async function handleGmbStore() {
      setPeriod(null)
      setImages([])

      if (currentStore?.cnpj && currentStore?.gmbStatus === 'INTEGRATED') {
        try {
          setIsLoadingGmb(true)

          const response = await api.gmbStore(currentStore.cnpj)
          const {
            data: { regularHours },
            media,
          } = response

          Array.isArray(response?.gmbAttributes?.attributes) && setGmbAttributes(response?.gmbAttributes?.attributes)
          setPeriod(convertPeriod(regularHours.periods))
          media?.mediaItems && setImages(media?.mediaItems)
        } catch (err) {
          setPeriod(null)
          setImages([])
          console.log('TCL: ~ handleGmbStore ~ err', err)
        } finally {
          setIsLoadingGmb(false)
        }
      } else {
        setPeriod(null)
      }
    }

    handleGmbStore()
  }, [currentStore?.cnpj])

  useEffect(() => {
    setProducts([])

    async function handleStoreProducts() {
      if (currentStore?.cnpj) {
        try {
          setIsLoading(true)

          const response = await api.getStoreProducts(currentStore.cnpj)
          setProducts(response.products)
        } catch (err) {
          console.log('TCL: ~ handleStoreProducts ~ err', err)
        } finally {
          setIsLoading(false)
        }
      }
    }

    handleStoreProducts()
  }, [currentStore?.cnpj])

  useEffect(() => {
    if (didMount && currentProduct?.gtin) history.push(`/produto/${currentProduct?.gtin}`)
  }, [position.user?.lat, position.user?.lng, currentProduct])

  const handleMoreProducts = useCallback(() => {
    if (!currentStore?.cnpj) return
    if (!showListProducts) {
      verMaisProdutos(config, currentStore, currentProduct, position?.user)
    } else {
      simpleBarRef.current?.scroll(0, 0)
    }

    setShowListProducts(!showListProducts)
  }, [currentStore, showListProducts])

  const handleGoRoute = useCallback(() => {
    cliqueRota(config, currentStore, currentProduct, position?.user)
  }, [currentStore, currentProduct, config, position])

  const handleSeeMoreProduct = useCallback(() => {
    setOpenMobileProducts(true)
    verMaisProdutos(config, currentStore, currentProduct, position?.user)
  }, [config, currentStore, currentProduct, position?.user])

  const handleOpenPeriod = useCallback(() => {
    setIsOpenPeriod(true)
    track('cliqueHorarios', { category: 'loja', name: currentStore?.name })
  }, [currentStore])

  const handleSeePhone = (type) => log({ event: 'call', type }, config, currentStore, currentProduct, position?.user)

  const handleProductClick = (product) => log({ event: 'product' }, config, currentStore, product, position?.user)

  const mapRouteCommunProps = useMemo(
    () => ({
      'aria-label': 'Route',
      onClick: handleGoRoute,
      'data-cy': 'route',
      data_analytics_category: 'onde-comprar',
      data_analytics_action: 'get_directions',
      data_analytics_partner_name: currentStore?.name,
      data_analytics_partner_address: currentStore?.coordinates?.formattedAddress,
      data_analytics_product_id: currentProduct?.gtin,
      data_analytics_product_name: currentProduct?.name,
      data_analytics_product_size: currentProduct?.name,
      data_analytics_product_type: currentProduct?.name,
      data_analytics_local: position?.user?.city,
    }),
    [currentStore, currentProduct, position],
  )

  const handleOpenDialogGmbAttibute = () => {
    track('cliqueGMBatributos')
    setOpenGmbAttribute(true)
  }

  const handleGoback = () => {
    if (isMobileStoreInformationsFullHeight) {
      dispatch(actions.minimazeStoreInformation())
      return
    }

    dispatch(actions.showStoreInformation(false))
  }

  return (
    <>
      <WhatsappFormDialog open={openForm} setOpen={setOpenForm} track={() => cliqueWhats(currentProduct)} />

      {isMobile && (
        <ProductListDialog
          open={openMobileProducts}
          setOpen={setOpenMobileProducts}
          products={products}
          handleProductClick={handleProductClick}
        />
      )}

      {/* dialog detalhes dos atributos do gmb */}
      <GmbAttributesDetailsDialog
        open={openGmbAttribute}
        setOpen={setOpenGmbAttribute}
        attributes={gmbAttributes}
        isMobile={isMobile}
      />

      {period && (
        <StoreDialogPeriod
          store={currentStore}
          weekdaysPeriod={period.weekdays}
          isOpen={isOpenPeriod}
          setIsOpen={setIsOpenPeriod}
        />
      )}

      {showStoreInformation && (
        <Paper
          style={{
            position: 'absolute',
            top: isMobile ? 'auto' : 15,
            bottom: isMobile ? (isMobileStoreInformationsFullHeight ? 0 : 40) : 'auto',
            right: isMobile ? 'auto' : 15,
            height: isMobile && isMobileStoreInformationsFullHeight ? '100%' : `auto`,
            maxHeight: isMobile ? 'auto' : '90%',
            left: 'auto',
            width,
            zIndex: 101,
          }}
        >
          <Box style={{ display: 'flex' }}>
            <Box style={{ flexGrow: 1, padding: '20px 16px' }}>
              <Box style={{ display: 'flex', gap: 16 }}>
                <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
                  {!!currentStore?.segmentation && <SegmentTag segment={currentStore.segmentation} />}
                  <Typography variant="body1" style={{ fontSize: 24, fontWeight: theme.typography.fontWeightBold }}>
                    {isMobile && (
                      <Tooltip title={getLiteral('1051')} placement="bottom">
                        <IconButton
                          aria-label={getLiteral('1051')}
                          size="small"
                          className={classes.back}
                          onClick={handleGoback}
                        >
                          <ArrowBack />
                        </IconButton>
                      </Tooltip>
                    )}
                    {currentStore?.name || ''} - {currentStore?.city}
                  </Typography>

                  <Typography variant="body1" style={{ fontSize: 16 }}>
                    {currentStore?.coordinates?.formattedAddress}
                  </Typography>

                  {!config.showContextViewEstablishment && (
                    <Chip
                      size="small"
                      label={currentStore?.stockMessage}
                      style={{ margin: '8px 0 16px', width: 'fit-content' }}
                    />
                  )}

                  {/* Exibição da previa dos gmb attributes mobile */}
                  {isMobile &&
                  isMobileStoreInformationsFullHeight &&
                  currentStore?.gmbStatus === 'INTEGRATED' &&
                  gmbAttributes.length > 0 ? (
                    <GmbAttributesPreview
                      handleOpen={handleOpenDialogGmbAttibute}
                      isMobile={isMobile}
                      gmbAttributes={gmbAttributes}
                    />
                  ) : null}

                  {!isMobile && period && <StorePeriod onClick={handleOpenPeriod} period={period} />}
                </Box>
              </Box>

              <Grid style={{ display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'column' }}>
                <Grid
                  style={{
                    display: 'flex',
                    padding: isMobile ? '0 8px' : 0,
                    gap: isMobile ? 0 : 32,
                  }}
                >
                  {isMobile && (
                    <MobileGmbStoreDetails
                      store={currentStore}
                      period={period}
                      images={images}
                      isLoading={isLoadingGmb}
                      isMobileStoreInformationsFullHeight={isMobileStoreInformationsFullHeight}
                      mapRouteCommunProps={mapRouteCommunProps}
                      config={config}
                      handleSeeMoreProduct={handleSeeMoreProduct}
                      currentProduct={currentProduct}
                      handleOpenPeriod={handleOpenPeriod}
                      position={position}
                    />
                  )}
                </Grid>

                {/* Exibição da previa dos attrributes desktop*/}
                {!isMobile && currentStore?.gmbStatus === 'INTEGRATED' && gmbAttributes.length > 0 ? (
                  <div style={{ margin: '16px 0 0' }}>
                    <GmbAttributesPreview
                      handleOpen={handleOpenDialogGmbAttibute}
                      isMobile={isMobile}
                      gmbAttributes={gmbAttributes}
                    />
                  </div>
                ) : null}

                {!isMobile ? (
                  <StoreContacts
                    color="primary"
                    currentStore={currentStore}
                    onSeePhone={handleSeePhone}
                    setOpenForm={setOpenForm}
                  />
                ) : null}
              </Grid>
            </Box>

            {!isMobile && <ButtonStoreRoute mapRouteCommunProps={mapRouteCommunProps} isMobile={isMobile} />}
          </Box>

          {isLoading && !config.showContextViewEstablishment && (
            <>
              <Divider />
              <Grid style={{ padding: 16 }}>
                <Skeleton animation="wave" variant="rect" width="100%" />
              </Grid>
            </>
          )}

          {!!products && products.length >= 1 && (
            <>
              {!isMobile && (
                <>
                  <Divider />
                  {!showListProducts && !config.showContextViewEstablishment && (
                    <Button
                      style={{ margin: isMobile ? 0 : '8px 16px', textTransform: 'none' }}
                      fullWidth={isMobile}
                      endIcon={<ArrowForward style={{ color: '#698387' }} />}
                      onClick={handleMoreProducts}
                    >
                      {getLiteral('2561')}
                    </Button>
                  )}
                </>
              )}

              {!isMobile && showListProducts && !config.showContextViewEstablishment && (
                <Grid style={{ padding: '12px 0 12px 31px' }}>
                  <Typography
                    variant="body1"
                    style={{ fontWeight: theme.typography.fontWeightMedium, marginBottom: 8 }}
                  >
                    {getLiteral('2475')}
                  </Typography>

                  <SimpleBar
                    autohide="true"
                    scrollableNodeProps={{ ref: simpleBarRef }}
                    style={{
                      maxHeight: 450,
                      overflowX: 'hidden',
                      overflowY: showListProducts ? 'inherit' : 'hidden',
                    }}
                  >
                    <ProductsGrid products={products} handleProductClick={handleProductClick} />
                  </SimpleBar>

                  <Button
                    variant="text"
                    style={{
                      textTransform: 'none',
                      textDecoration: 'underline',
                    }}
                    onClick={() => setShowListProducts(false)}
                  >
                    {getLiteral('2476')}
                  </Button>
                </Grid>
              )}
            </>
          )}

          {isMobile && !isMobileStoreInformationsFullHeight ? (
            <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="text"
                startIcon={<MoreHoriz />}
                onClick={() => dispatch(actions.maximazeStoreInformation())}
              >
                {getLiteral('1804')}
              </Button>
            </Box>
          ) : null}
        </Paper>
      )}
    </>
  )
}

export default StoreInformations
